import { assignEmployeeSalary, createTemplate, getTemplates, updateTemplate } from "../../../SalaryApi";
import { v2UpdateComponent } from "../../SalaryApiV2";
import { calcOf, formulaOf } from "../../wizard/shared";
import {
  resyncPipelineTemplateComponents,
  syncPipelineTemplateComponents,
} from "./syncPipelineTemplateComponents";

/**
 * Persist entire pipeline draft in one transaction (final submit only).
 */
export async function submitSalaryTemplatePipeline(state, componentRows = []) {
  const name = String(state.templateName || "").trim();
  if (!name) throw new Error("Salary template name is required.");

  const ids = [...new Set((state.allocatedComponentIds || []).map(String))];
  if (!ids.length) throw new Error("Select at least one component.");

  const byId = new Map((componentRows || []).map((r) => [String(r.component_id), r]));

  for (const id of ids) {
    const comp = byId.get(id);
    if (!comp) continue;
    const stored = String(comp.formula_expression || comp.formula || "").trim();
    const desired = String(state.formulas?.[id]?.expression ?? "").trim() || String(formulaOf(comp) || "").trim();
    if (desired && desired !== stored) {
      await v2UpdateComponent(comp.component_id, {
        calculation_type: "formula",
        formula_expression: desired,
      });
      comp.formula_expression = desired;
      comp.calculation_type = "formula";
    } else if (calcOf(comp) === "formula" && !stored) {
      comp.calculation_type = "fixed";
    }
  }

  const templates = await getTemplates().catch(() => []);
  const list = Array.isArray(templates) ? templates : [];
  const matchedById = state.templateId
    ? list.find((t) => String(t.template_id) === String(state.templateId))
    : null;
  const matchedByName = list.find(
    (t) => String(t.name || "").trim().toLowerCase() === name.toLowerCase(),
  );
  const existing = matchedById || matchedByName || null;
  let templateId = existing?.template_id ? String(existing.template_id) : null;
  const isEdit = Boolean(existing && templateId);

  if (isEdit) {
    await updateTemplate(templateId, {
      name,
      description: String(state.templateDescription || "").trim() || null,
    });
  } else {
    const tpl = await createTemplate({
      name,
      description: String(state.templateDescription || "").trim() || undefined,
    });
    templateId = tpl?.template_id || tpl?.id;
    if (!templateId) throw new Error("Template was created but no ID was returned.");
  }

  const orderedComps = ids.map((id) => byId.get(id)).filter(Boolean);
  if (isEdit) {
    await resyncPipelineTemplateComponents(
      templateId,
      ids,
      orderedComps.length ? orderedComps : componentRows
    );
  } else {
    await syncPipelineTemplateComponents(
      templateId,
      ids,
      orderedComps.length ? orderedComps : componentRows
    );
  }

  const drafts = Array.isArray(state.draftEmployeeAssignments) ? state.draftEmployeeAssignments : [];
  const today = new Date().toISOString().slice(0, 10);
  for (const row of drafts) {
    const employeeId = row?.employee_id;
    // Prefer each employee's own package; fall back to structure target only when missing.
    const rawCtc = row?.ctc != null && row?.ctc !== "" ? row.ctc : state.targetAnnualCtc;
    const ctc = Number(String(rawCtc ?? "").replace(/,/g, ""));
    if (!employeeId || !Number.isFinite(ctc) || ctc <= 0) continue;
    // eslint-disable-next-line no-await-in-loop
    await assignEmployeeSalary({
      employee_id: employeeId,
      template_id: templateId,
      ctc,
      effective_from: row.effective_from || today,
    });
  }

  return { template_id: templateId, name, updated: Boolean(isEdit && templateId) };
}
